import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	fetchWarmupCatalog,
	fetchWarmupRoutine,
	saveWarmupRoutine,
	logLessonWarmups,
	fetchLastLessonWarmups,
} from '../../../../services/WarmupRoutineService';
import { assembleRoutine } from '../../../../services/warmupRoutineModel';
import {
	isDirty,
	describeChanges,
	applyReachedTop,
	newRoutineForStudent,
	routineFromDoc,
	readDraft,
	writeDraft,
	clearDraft,
	summarizeLastLesson,
} from '../../../../services/warmupDraftModel';

/**
 * The student's warmup routine inside a lesson: SAVED vs DRAFT, the reached
 * tops, and the teacher-only log — the state behind branch 2 of the plan.
 *
 * SAVED is `warmupRoutines/{studentId}` (or nothing on a first lesson). DRAFT
 * is what the player shows. A swap, a skip, a settings change or a reached top
 * edits the draft; nothing reaches the document until `save()`. `discard()`
 * puts the saved routine back. The draft is kept per lesson in sessionStorage
 * (same rules as useLessonScratch), so leaving the dashboard and coming back
 * through the return pill loses nothing — and the wrap-up sheet asks once if
 * the draft is still unsaved when the lesson completes.
 *
 * FIRST LESSON: no document yet → a routine is proposed from the student's
 * age band and saved on the first Play (the plan's step 5), so the "New
 * routine" line disappears the moment it is actually used.
 *
 * THE LOG is written on every exercise end onto the session record (teacher-
 * only, per Anthony), debounced — one map, replaced whole, this hook owns it.
 * Playing a warmup is NOT attendance: nothing here touches outcome/status.
 */
const LOG_DEBOUNCE_MS = 800;

export default function useWarmupRoutine({ studentId, student, lessonId, uid }) {
	const [catalog, setCatalog] = useState([]);
	const [catalogError, setCatalogError] = useState('');
	const [loading, setLoading] = useState(true);
	const [saved, setSaved] = useState(null);
	const [draft, setDraft] = useState(null);
	const [current, setCurrent] = useState(0);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState('');
	const [lastLesson, setLastLesson] = useState(null);
	const [log, setLog] = useState({ exercises: [], seconds: 0 });
	const logTimer = useRef(null);
	const savedRef = useRef(null);
	savedRef.current = saved;
	// The log flush reads the cell through a ref so `onExerciseEnd` keeps one
	// identity for the whole lesson — the player treats a new identity as a
	// reason to end the current run (see WarmupPlayer's unmount effect).
	const draftRef = useRef(null);
	draftRef.current = draft;

	const byId = useMemo(() => new Map(catalog.map((c) => [c.id, c])), [catalog]);

	// Catalog once per mount.
	useEffect(() => {
		let cancelled = false;
		fetchWarmupCatalog()
			.then((rows) => {
				if (!cancelled) setCatalog(rows);
			})
			.catch((err) => {
				console.error('Warmup catalog failed to load:', err);
				if (!cancelled) setCatalogError('The warmup catalog did not load.');
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// Saved routine + any kept draft + last lesson, once the catalog and student are known.
	useEffect(() => {
		if (!studentId) {
			// No student on this record (an unlinked or old-style test lesson): a
			// routine is per student, so there is nothing to load. Report it as a
			// state rather than spinning — LessonWarmups says what to do.
			setSaved(null);
			setDraft(null);
			setLoading(false);
			return undefined;
		}
		if (!catalog.length) return undefined;
		let cancelled = false;
		setLoading(true);
		Promise.all([fetchWarmupRoutine(studentId), fetchLastLessonWarmups(studentId, lessonId)])
			.then(([docData, last]) => {
				if (cancelled) return;
				const savedRoutine = routineFromDoc(docData);
				const kept = readDraft(lessonId);
				setSaved(savedRoutine);
				if (kept && kept.studentId === studentId) {
					setDraft({ cell: kept.cell, items: kept.items, band: kept.band || null, seconds: kept.seconds || 0, inBand: true, error: null });
					setCurrent(Number.isInteger(kept.current) ? kept.current : 0);
				} else {
					setDraft(savedRoutine || newRoutineForStudent(student, catalog));
					setCurrent(0);
				}
				setLastLesson(last);
				if (last && last.warmups && last.lessonId === lessonId) setLog(last.warmups);
			})
			.catch((err) => {
				console.error('Warmup routine failed to load:', err);
				if (!cancelled) {
					setSaved(null);
					setDraft(newRoutineForStudent(student, catalog));
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
		// `student` is the doc object; its identity changes with the fetch, but
		// only its age/birthday matter and those are read at proposal time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [studentId, lessonId, catalog]);

	const dirty = isDirty(saved, draft);
	const changes = useMemo(() => (dirty ? describeChanges(saved, draft, byId) : []), [dirty, saved, draft, byId]);

	// Keep the draft across a route change while it is unsaved; drop it once clean.
	useEffect(() => {
		if (!lessonId || loading) return;
		if (dirty && draft) writeDraft(lessonId, { studentId, cell: draft.cell, items: draft.items, band: draft.band, seconds: draft.seconds, current });
		else clearDraft(lessonId);
	}, [lessonId, studentId, dirty, draft, current, loading]);

	const setRoutine = useCallback((next) => setDraft(next), []);

	const setCell = useCallback(
		(cell) => {
			const r = assembleRoutine(cell, catalog);
			setDraft({ cell, items: r.items, seconds: r.seconds, band: r.band, inBand: r.inBand, error: r.error });
			setCurrent(0);
		},
		[catalog]
	);

	const save = useCallback(async () => {
		if (!studentId || !draft) return false;
		setSaving(true);
		setSaveError('');
		try {
			const items = draft.items.map(({ aboveBand, ...it }) => it);
			await saveWarmupRoutine(studentId, { cell: draft.cell, items }, uid);
			const savedNow = { ...draft, items };
			setSaved(savedNow);
			setDraft(savedNow);
			clearDraft(lessonId);
			return true;
		} catch (err) {
			console.error('Warmup routine did not save:', err);
			setSaveError('The routine did not save. Try again.');
			return false;
		} finally {
			setSaving(false);
		}
	}, [studentId, draft, uid, lessonId]);

	const discard = useCallback(() => {
		setDraft(saved || newRoutineForStudent(student, catalog));
		setCurrent(0);
		setSaveError('');
		clearDraft(lessonId);
	}, [saved, student, catalog, lessonId]);

	/** First Play on a student with no document: save the proposed routine as-is. */
	const onExerciseStart = useCallback(async () => {
		if (savedRef.current || !draft || !studentId) return;
		await save();
	}, [draft, studentId, save]);

	const flushLog = useCallback(
		(next) => {
			clearTimeout(logTimer.current);
			logTimer.current = setTimeout(() => {
				logLessonWarmups(lessonId, { exercises: next.exercises, seconds: next.seconds, cell: draftRef.current?.cell || null }).catch((err) =>
					console.error('Warmup log did not save:', err)
				);
			}, LOG_DEBOUNCE_MS);
		},
		[lessonId]
	);

	/**
	 * An exercise finished (descended home, was moved past, or was stopped).
	 * `topReached` is the UN-shifted top (the player subtracts octaveShift), so
	 * a drop-octave-then-climb never counts (Anthony: "I agree about the octave").
	 */
	const onExerciseEnd = useCallback(
		(result) => {
			if (!result) return;
			const { index, exerciseId, tonicStart, topReached, steps, octaveShift, guideLevel, seconds, skipped } = result;
			if (!skipped && Number.isFinite(topReached) && steps > 0) {
				setDraft((prev) => applyReachedTop(prev, index, topReached, byId));
			}
			setLog((prev) => {
				const entry = { index, exerciseId, tonicStart: tonicStart ?? null, topReached: Number.isFinite(topReached) ? topReached : null, steps: steps || 0, octaveShift: octaveShift || 0, guideLevel: guideLevel || 'full', seconds: Math.round(seconds || 0), skipped: Boolean(skipped) };
				const exercises = prev.exercises.filter((e) => e.index !== index).concat(entry).sort((a, b) => a.index - b.index);
				const next = { exercises, seconds: exercises.reduce((a, e) => a + (e.seconds || 0), 0) };
				if (lessonId) flushLog(next);
				return next;
			});
		},
		[byId, lessonId, flushLog]
	);

	useEffect(() => () => clearTimeout(logTimer.current), []);

	const lastLessonLine = useMemo(() => (lastLesson && lastLesson.lessonId !== lessonId ? summarizeLastLesson(lastLesson, byId) : ''), [lastLesson, lessonId, byId]);

	return {
		catalog,
		catalogError,
		loading,
		hasStudent: Boolean(studentId),
		routine: draft,
		saved,
		dirty,
		changes,
		saving,
		saveError,
		save,
		discard,
		setRoutine,
		setCell,
		current,
		setCurrent,
		onExerciseStart,
		onExerciseEnd,
		log,
		lastLessonLine,
	};
}
